// System dependencies

import { encode } from 'base-64';
import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { format, parseISO } from 'date-fns';
import { createHash, randomBytes } from 'crypto';
import { v4 as uuidv4 } from 'uuid';

// Application dependencies

import store, { AppDispatch } from '@/redux/store';
import { AddressCrud, AddressFields, AddressTypes } from '@/interfaces/address.interfaces';
import { AuthorisationFields, AuthorisationProduct, AuthorisationRedux, AuthorisationRole } from '@/interfaces/authorisation/authorisation.interfaces';
import { ConfigurationDetailAutoIt, ConfigurationFields, ConfigurationType, ConfigurationTypes } from '@/interfaces/authorisation/configuration.interfaces';
import { DataTableControl } from '@/interfaces/dataTable.interfaces';
import { FORM_INPUT_CASE_EXCEPTIONS, FORM_INPUT_CASE_PATTERNS } from '@/interfaces/formInput.interfaces';
import { FormInputComboItem } from '@/components/form/formInputCombo/FormInputCombo';
import { FormSpinnerFields } from '@/interfaces/formSpinner.interfaces';
import { FormSpinnerPageProps } from '@/components/form/formSpinner/FormSpinnerPage';
import { RouteFields, RouteNames } from '@/interfaces/route.interfaces';
import { activateFormSpinner, deactivateFormSpinner } from '@/redux/formSpinner/formSpinner.slices';
import { authorisationLoadAuthorisation } from '@/redux/authorisation/authorisation.slices';
import { dbAuthorisation } from './dbAuthorisation';
import { devConsole } from './devConsole';
import { userLoadNonPortalProduct, userLoadPortalProduct, userLoadUser } from '@/redux/user/user.slices';
import { UserFields } from '@/interfaces/authorisation/user.interfaces';

// Namespace

const NAMESPACE = 'functions';

// Constants

export const PORTAL_ABOUT = 'portal_about';
export const PORTAL_NOT_AUTHORISED = 'portal_not_authorised';

// local control values

const spinnerTimeout = parseInt(process.env.NEXT_PUBLIC_FORM_SPINNER_TIMEOUT || '1500', 10);
let timeoutId: NodeJS.Timeout | null = null;

/**
 * Page view guard. Under the Entra two-tier model both ADMIN and VIEWER may view every private page — editing is gated separately (the
 * UI hides edit controls via isReadOnly/canEdit and the server actions enforce assertEditAccess). This only redirects to the dashboard
 * when there is no recognised role (e.g. an unauthenticated or unauthorised session). Retained under its original name for the pages that
 * previously gated on a company administrator.
 *
 * @param {AuthorisationRedux} authorisation
 * @returns {booleans}
 */

export const allowCompanyAdmin = (authorisation: AuthorisationRedux, router: AppRouterInstance) => {
  // Spread out the role from the current authorisation.

  const { role } = authorisation.authorisation || {};

  // Let any recognised role (ADMIN or VIEWER) view the page; editing remains gated by isReadOnly/canEdit and the server actions.

  if (role !== AuthorisationRole.ADMIN && role !== AuthorisationRole.VIEWER) {
    // No recognised role, so we set the spinner and push to the dashboard.

    manageFormSpinner(`loading ${RouteNames[RouteFields.Dashboard].name} page - please wait`);
    router.push(RouteNames[RouteFields.Dashboard].route);
  } else {
    // The user is allowed so we can clear the spinner.

    clearFormSpinner();
  }
};

/**
 * Page view guard. Identical to allowCompanyAdmin under the two-tier model: both ADMIN and VIEWER may view, editing is gated separately.
 *
 * @param {AuthorisationRedux} authorisation
 * @returns {booleans}
 */

export const allowPortalAdmin = (authorisation: AuthorisationRedux, router: AppRouterInstance) => {
  // Spread out the role from the current authorisation.

  const { role } = authorisation.authorisation || {};

  // Let any recognised role (ADMIN or VIEWER) view the page; editing remains gated by isReadOnly/canEdit and the server actions.

  if (role !== AuthorisationRole.ADMIN && role !== AuthorisationRole.VIEWER) {
    // No recognised role, so we set the spinner and push to the dashboard.

    manageFormSpinner(`loading ${RouteNames[RouteFields.Dashboard].name} page - please wait`);
    router.push(RouteNames[RouteFields.Dashboard].route);
  } else {
    // The user is allowed so we can clear the spinner.

    clearFormSpinner();
  }
};

// ---------------------------------------------------------------------------------------------------------------------------------------
// Role based access control (RBAC)
// ---------------------------------------------------------------------------------------------------------------------------------------

/**
 * Roles that are granted full read, edit and configure access. Every other role is treated as read only (view only) per the business
 * requirement that Portal Administrators can edit and configure while Guest/Read Only users can view without making changes.
 */

export const EDIT_ROLES: AuthorisationRole[] = [AuthorisationRole.ADMIN];

/**
 * Determines whether a given role is allowed to edit. Returns true only for roles present in EDIT_ROLES.
 *
 * @param {AuthorisationRole | null | undefined} role - The role to test.
 * @returns {boolean} - True when the role has edit access, otherwise false.
 */

export const canEditRole = (role?: AuthorisationRole | null): boolean => !!role && EDIT_ROLES.includes(role);

/**
 * Determines whether the current authorisation is allowed to edit (create/update/delete). Read only users (anyone not in EDIT_ROLES)
 * return false so the UI can hide or disable edit controls.
 *
 * @param {AuthorisationRedux} authorisation - The current authorisation redux state.
 * @returns {boolean} - True when the current authorisation has edit access, otherwise false.
 */

export const canEdit = (authorisation: AuthorisationRedux): boolean => canEditRole(authorisation.authorisation?.[AuthorisationFields.Role] ?? null);

/**
 * Convenience inverse of canEdit. Returns true when the current authorisation is read only (view only).
 *
 * @param {AuthorisationRedux} authorisation - The current authorisation redux state.
 * @returns {boolean} - True when the current authorisation is read only, otherwise false.
 */

export const isReadOnly = (authorisation: AuthorisationRedux): boolean => !canEdit(authorisation);

/**
 * Converts blank, NaN, or undefined values to null.
 *
 * @template T - The type of the input value.
 * @param {T} value - The input value to be checked and potentially converted.
 * @returns {T | null} - Returns null if the value is blank (string), NaN (number), an invalid date, or undefined/null; otherwise,
 * returns the original value.
 */

export const blankToNull = <T>(value: T): T | null => {
  if (typeof value === 'string' && value.trim() === '') {
    return null;
  }
  if (typeof value === 'number' && isNaN(value)) {
    return null;
  }
  if (value instanceof Date && isNaN(value.getTime())) {
    return null;
  }
  if (value === undefined || value === null) {
    return null;
  }
  return value;
};

/**
 * Checks whether an address needs to be deleted based on the presence of an address ID and a null address.
 * If the address ID is present and the address is null, the address record is deleted.
 *
 * @param {AddressCrud | null} addressCrud - The address object that can be null.
 * @param {number | null} addressId - The ID of the address that can be null.
 * @returns {Promise<void>} - A promise that resolves when the operation is complete.
 */

export const checkForAddressDelete = async (addressCrud: AddressCrud | null, addressId: number | null): Promise<void> => {
  // This method checks to see whether we need to delete an address. If the address is null and the addressId has a value that signifies
  // that the address is no longer required.

  try {
    // Check for a null address id or an address id and an address. If we have either case we can return doing nothing else.

    if (!addressId || (addressId && addressCrud)) return;

    // To be here we have an address id and a null address so we can delete the address record.
    await dbAuthorisation.address.delete({ where: { id: addressId } });
  } catch (error) {
    // Catch the unexpected error, let the user know and return.

    devConsole.error(NAMESPACE, `checkForAddressDelete`, `Unexpected error - ${formatErrorMessage(error)}`);
    return;
  }
};

/**
 * Ensures the form spinner remains active for a minimum duration. This function calculates the time elapsed since the spinner was activated
 * and ensures that the spinner remains visible for at least NEXT_PUBLIC_FORM_SPINNER_TIMEOUT from the start time. If the spinner has
 * already been active for more than NEXT_PUBLIC_FORM_SPINNER_TIMEOUT, it will be deactivated immediately.
 *
 * The function clears any existing timeout to avoid overlapping timers and sets a new timeout for the remaining duration. Once the timeout
 * expires, it deactivates the spinner using `manageFormSpinner`.
 *
 * @returns {void}
 */

export const clearFormSpinner = () => {
  // If there is a timeoutId there is already a timer running so we can shut it down and reset the timeoutId.

  if (timeoutId) {
    clearTimeout(timeoutId);
    timeoutId = null;
  }

  // Get the formSpinner state.

  const state = store.getState().formSpinner;

  // If the current state.isActive is false we can exit doing nothing else.

  if (!state.isActive) return;

  // Return the elapsed time since the spinner started and calculate the remaining time. If the remaining time calculation is less than 0,
  // 0 will be applied.

  const elapsedTime = Date.now() - (state.startTime || 0);
  const remainingTime = Math.max(spinnerTimeout - elapsedTime, 0);

  // Check whether the remainingTime <= 0 and, if so, turn off the spinner, clear the timeoutId and exit.

  if (remainingTime <= 0) {
    manageFormSpinner(null);
    timeoutId = null;
    return;
  }

  // To be here there is remainingTime needed so set the timeoutId and start the timer for ramaining time. On completion the timer will
  // clear the spinner and the timeoutId.

  timeoutId = setTimeout(() => {
    manageFormSpinner(null);
    timeoutId = null;
  }, remainingTime);
};

/**
 * Concatenates two strings with an optional separator.
 *
 * @param firstField The first string to concatenate.
 * @param secondField (optional) The second string to concatenate. Defaults to an empty string.
 * @param separator (optional) The separator to insert between the concatenated fields. Defaults to an empty string.
 * @returns The combined string with the optional separator.
 */

export const concatFields = (firstField: string, secondField: string, separator: string = '') => {
  return `${firstField}${secondField !== '' ? separator + secondField : ''}`;
};

/**
 * Converts ConfigurationTypes mapping into a FormInputComboItem array for use in the FormInputCombo dropdown's items object.
 *
 * @returns {FormInputComboItem[]} Array of combo items with value and label.
 */

export const configurationTypesToComboItems = (): FormInputComboItem[] => {
  return Object.values(ConfigurationType).map((key) => ({
    value: key,
    label: ConfigurationTypes[key]?.[ConfigurationFields.Title] ?? key,
  }));
};

/**
 * Creates or updates an address in the database.
 *
 * @param {AddressCrud | null} addressCrud - The address data to create or update. We have to use any as we're dealing with raw data.
 * @param {number | null} addressId - The ID of the address to update. If null, a new address will be created.
 * @returns {Promise<number | null>} - A promise that resolves to the ID of the created or updated address, or null if the address is null.
 */

export const createOrUpdateAddress = async (addressCrud: AddressCrud | null): Promise<number | null> => {
  try {
    // See whether we have an address and, if not, return null.

    if (!addressCrud) {
      return null;
    }

    // Destructure the id from the address to avoid updating the identity column

    const { id, ...addressData } = addressCrud;

    // Check to see if we are updating or creating an address.

    if (id) {
      // There is an id so we can update the record. Update the record with the remaining address data.

      await dbAuthorisation.address.update({
        where: { id },
        data: { ...addressData },
      });

      // And just return the same id.
      return id;
    } else {
      // To be here we have no id but we have an address object so we can insert the record.

      const newAddress = await dbAuthorisation.address.create({
        data: { ...addressData },
      });

      // And return the new id.

      return newAddress.id;
    }
  } catch (error) {
    // Catch the unexpected error, let the user know and return.

    devConsole.error(NAMESPACE, `createOrUpdateAddress`, `Unexpected error - ${formatErrorMessage(error)}`);
    return null;
  }
};

/**
 * Transforms a TypeScript enum into an array of objects formatted according to the FormInputComboItem interface.
 * Supports both numeric and string enums. For numeric enums, reverse-mapped keys are excluded automatically.
 * Each enum entry becomes a combo item with its original key as the label and its value as the value.
 *
 * This is particularly useful for generating select dropdown options or similar UI elements
 * that require a value-label pairing.
 *
 * @param {Record<string, string | number>} enumObj - The enum object to transform. Supports both string and numeric enums.
 * @returns {FormInputComboItem[]} An array of objects, each containing a `value` (number or string) and a `label` (string).
 */

export const enumToComboList = <T extends Record<string, string | number>>(enumObj: T): FormInputComboItem[] => {
  return Object.keys(enumObj)
    .filter((key) => isNaN(Number(key))) // Exclude numeric reverse keys (from numeric enums)
    .map((key) => ({
      value: enumObj[key] as string | number,
      label: key,
    }));
};

/**
 * Takes the address and, if it is null, returns an object with undefined values ready for editing,
 *
 * @param {AddressCrud | null} address - The address object to format or a null.
 * @param {AddressTypes} addressType - The address type to set a null object to.
 * @return {AddressCrud} - The formatted object. If the input object was null it will be an object with undefined values.
 */

export const formatAddress = (addressCrud: AddressCrud | null, addressType: AddressTypes): AddressCrud => {
  // Check to see that we have an address object that is populated and, if so, return it.

  if (addressCrud) {
    return addressCrud;
  }

  // To be here the object is null so we can return an object with undefined fields.

  return {
    [AddressFields.AddressType]: addressType,
    [AddressFields.AdminArea1]: undefined,
    [AddressFields.AdminArea2]: undefined,
    [AddressFields.Country]: undefined,
    [AddressFields.Id]: undefined,
    [AddressFields.Latitude]: undefined,
    [AddressFields.Locality]: undefined,
    [AddressFields.Longitude]: undefined,
    [AddressFields.PostalCode]: undefined,
    [AddressFields.PlaceId]: undefined,
    [AddressFields.Route]: undefined,
    [AddressFields.StreetNumber]: undefined,
    [AddressFields.SubPremise]: undefined,
  };
};

/**
 * Creates a formatted address string from the given address object.
 *
 * @param {AddressCrud | null} addressCrud - The address object containing the address components or null.
 * @returns {string} - The formatted address string resembling the Google Maps format or a blank string if the address object is null.
 */

export const formatAddressString = (addressCrud: AddressCrud | null): string => {
  // Check to see that we have an address object that is populated and, if not, return a blank string.

  if (!addressCrud) {
    return '';
  }

  // Create an array to hold the parts of the address

  const addressParts: string[] = [];

  // Capitalize the first letter of subPremise if it exists

  if (addressCrud[AddressFields.SubPremise]) {
    addressParts.push(addressCrud[AddressFields.SubPremise].charAt(0).toUpperCase() + addressCrud[AddressFields.SubPremise].slice(1));
  }

  if (addressCrud[AddressFields.StreetNumber]) {
    addressParts.push(`${addressCrud[AddressFields.StreetNumber]} ${addressCrud[AddressFields.Route] || ''}`.trim());
  } else if (addressCrud[AddressFields.Route]) {
    addressParts.push(addressCrud[AddressFields.Route]);
  }

  if (addressCrud[AddressFields.Locality] || addressCrud[AddressFields.AdminArea1]) {
    // Join locality and admin_area_1 with a space

    addressParts.push(`${addressCrud[AddressFields.Locality] ? addressCrud[AddressFields.Locality] + ' ' : ''}${addressCrud[AddressFields.AdminArea1] || ''}`.trim());
  }

  if (addressCrud[AddressFields.AdminArea2]) {
    addressParts.push(addressCrud[AddressFields.AdminArea2]);
  }

  if (addressCrud[AddressFields.PostalCode]) {
    addressParts.push(addressCrud[AddressFields.PostalCode]);
  }

  if (addressCrud[AddressFields.Country]) {
    addressParts.push(addressCrud[AddressFields.Country]);
  }

  // Join the address parts with a comma and space

  const addressString = addressParts.join(', ');

  // Return the formatted address string

  return addressString;
};

/**
 * Formats an error message for display, handling known auth error cases and generic errors.
 *
 * @param {unknown} error - The error object to be formatted, which may be an AuthError, a generic Error, or an unknown type.
 * @returns {string} - A formatted error message string that is user-friendly and informative.
 */

export const formatErrorMessage = (error: unknown): string | null => {
  if (error === null) {
    return null;
  }
  if (typeof error === 'object' && error !== null && 'message' in error && typeof (error as { message: unknown }).message === 'string') {
    return (error as { message: string }).message;
  } else if (error instanceof Error) {
    // Handle generic errors
    return `Error: ${error.message}`;
  } else {
    // Fallback for errors that don't fit in the above categories
    return 'An unexpected error occurred';
  }
};

/**
 * Converts an ISO date string to a formatted date string in local time.
 * The output format is "YYYY-MM-DD HH:mm".
 *
 * @param {string} isoDateString - The ISO string to be formatted.
 * @returns {string} The date formatted as "YYYY-MM-DD HH:mm".
 */

export const formatISODate = (isoDateString: string | null): string => {
  // Make sure that we have an isoDateString. If not return '--'
  if (!isoDateString) {
    return '--';
  }
  // We have an isoDateString so we can format it to local time. Note that this
  // process will automatically adjust the UTC time to the local time zone settings
  // configured in the operating system
  const date = parseISO(isoDateString);
  return format(date, 'yyyy-MM-dd HH:mm');
};

/**
 * Capitalises an entry string according to common capitalisation rules. This method handles special cases such as names with prefixes
 * like "O'", "D'", "Mac", and "Mc", and maintains proper casing for exceptions like "van der", "de", "le", and "von".
 *
 * It may be customised and enhanced as new use cases are discovered.
 *
 * @param {string} name - The name string to capitalize.
 * @returns {string} - The capitalized name string.
 */

export const formatTitleCase = (entry: string): string => {
  // Normalise the entry and split it into words.

  const words = entry.toLowerCase().split(' ');

  // Check for multi-word exceptions first.

  for (let i = 0; i < words.length; i++) {
    const twoWords = `${words[i]} ${words[i + 1]}`;
    const threeWords = `${words[i]} ${words[i + 1]} ${words[i + 2]}`;

    if (FORM_INPUT_CASE_EXCEPTIONS.includes(threeWords)) {
      words[i] = threeWords;
      words.splice(i + 1, 2); // Remove the next two words since they are part of the exception.
    } else if (FORM_INPUT_CASE_EXCEPTIONS.includes(twoWords)) {
      words[i] = twoWords;
      words.splice(i + 1, 1); // Remove the next word since it's part of the exception.
    }
  }

  // Now process the words to create the capitalise format.

  return words
    .map((word, index) => {
      if (FORM_INPUT_CASE_EXCEPTIONS.includes(word) && index !== 0) {
        return word; // Keep the exception word in lowercase.
      }

      // Handle apostrophe rule: capitalize both parts around the apostrophe.

      if (word.includes("'")) {
        return word
          .split("'")
          .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
          .join("'");
      }

      // Handle special prefixes like "O'", "D'", "Mac", "Mc".

      for (const { prefix, capitaliseNext, specialCase } of FORM_INPUT_CASE_PATTERNS) {
        if (word.startsWith(prefix)) {
          if (specialCase && word.length > prefix.length) {
            // Capitalize "Mac" or "Mc" prefix and the next letter (e.g., "MacDonald").
            return prefix.charAt(0).toUpperCase() + prefix.slice(1) + word.charAt(prefix.length).toUpperCase() + word.slice(prefix.length + 1);
          }
          if (capitaliseNext) {
            // Capitalize the first letter after the prefix (e.g., "O'Hara", "D'Angelo").

            return prefix.charAt(0).toUpperCase() + prefix.slice(1) + word.charAt(prefix.length).toUpperCase() + word.slice(prefix.length + 1);
          }
        }
      }

      // Default capitalisation.

      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

/**
 * Generates an 8-character random code.
 *
 * @returns {string} The generated 8-character code.
 */

export const generate8CharCode = (): string => {
  // Generate 4 random bytes
  const array = randomBytes(4);
  // Convert the array to a hexadecimal string
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Generates a UUID without hyphens and encodes it to Base64.
 *
 * @returns {string} The Base64 encoded UUID without hyphens.
 */

export const generateBase64Uuid = (encodeUuid: boolean = true): string => {
  // Generate a UUID and remove hyphens.
  const uuid = uuidv4().replace(/-/g, '');
  // Return the encoded UUID if encodeUuid is true; otherwise, return the raw UUID.
  return encodeUuid ? encode(uuid) : uuid;
};

/**
 * Generates an MD5 hash from a combination of a private key and a public key.
 *
 * @param {string} privateKey - The private key used in the hash generation.
 * @param {string} publicKey - The public key used in the hash generation.
 * @returns {string} - The generated MD5 hash as a hexadecimal string.
 */

export const generateMd5Hash = (privateKey: string, publicKey: string): string => {
  const combinedKeys = privateKey + publicKey;
  const md5Hash = createHash('md5').update(combinedKeys).digest('hex');
  return md5Hash;
};

/**
 * Generates a UUID and optionally converts it to uppercase.
 *
 * @param {boolean} [uppercase=false] - Whether to convert the UUID to uppercase.
 * @returns {string} The generated UUID, optionally in uppercase.
 */

export const generateUuid = (): string => {
  // Generate a uuid.
  const uuid = uuidv4();
  // Return the uuid.
  return uuid;
};

/**
 * Takes in a production name and matches it to the corresponding image file. If there is
 * no match it returns the unknown-product.png.
 *
 * @param {string} productName - The product name to match for the image.
 * @returns {string} The image file name matching the product name.
 */

export const getAuthorisationImage = (productName: string) => {
  // Instantiate the cardImage.

  let cardImage;

  // Check to see whether we have a matching product name and assign the image.

  switch (productName) {
    case PORTAL_ABOUT:
      cardImage = '/assets/core/core_about.png';
      break;
    case PORTAL_NOT_AUTHORISED:
      cardImage = '/assets/core/core_unauthorised.png';
      break;
    case AuthorisationProduct.CLOUD_ADMIN:
      cardImage = '/assets/core/core_cloud_admin.png';
      break;
    case AuthorisationProduct.PARTS_PORTAL:
      cardImage = '/assets/core/core_parts_portal.png';
      break;
    case AuthorisationProduct.PORTAL_ADMIN:
      cardImage = '/assets/core/core_portal_admin.png';
      break;
    case AuthorisationProduct.PORTAL_TEMPLATE:
      cardImage = '/assets/core/core_portal_template.png';
      break;
    case AuthorisationProduct.SERVICE_PORTAL:
      cardImage = '/assets/core/core_service_portal.png';
      break;
    default:
      cardImage = '/assets/core/core_unknown_product.png';
  }

  // Return the card image accordingly.

  return cardImage;
};

/**
 * Takes in a authorisation role and returns a decription. If there is no match it returns the unknown role message.
 *
 * @param {string} role - The authorisation role to match for the description.
 */

export const getAuthorisationRoleDescription = (role: AuthorisationRole) => {
  // Instantiate the description

  let description = 'Unknown Role';

  // Check to see whether we have a matching role and assign the description.

  switch (role) {
    case AuthorisationRole.ADMIN:
      description = 'Administrator';
      break;
    case AuthorisationRole.VIEWER:
      description = 'Viewer';
      break;
  }

  // Return the description

  return description;
};

/**
 * Retrieves the user ID from the store's user state. Note it must be called from a component.
 *
 * @returns {number | null} The user ID if available, otherwise null.
 */

export const getUserId = (): number | null => {
  const state = store.getState().user; // Get current store state

  devConsole.log(NAMESPACE, `getUserId`, `state: ${JSON.stringify(state)}`);

  return state[UserFields.User] ? state[UserFields.User][UserFields.Id] : null;
};

/**
 * Manages the form spinner state by dispatching an action to the Redux store.
 *
 * @param {string} caption | null - a field to display the caption for the spinner or null to turn it off. Sending up a blank string is
 * allowable to provide for the spinner without any caption.
 */

export const manageFormSpinner = (caption: string | null) => {
  if (caption !== null) {
    store.dispatch(
      activateFormSpinner({
        caption,
      }),
    );
  } else {
    store.dispatch(deactivateFormSpinner());
  }
};

/**
 * Updates the FormSpinnerPage state.
 *
 * @param caption - The caption to display in the spinner, or null if no caption. Note that an empty string ("") is acceptable and will result
 * in the spinner but no qualifying text.
 * @param setSpinnerState - The state setter for the FormSpinnerPage state.
 */

export const manageFormSpinnerPage = (caption: string | null, setSpinnerState: React.Dispatch<React.SetStateAction<FormSpinnerPageProps | null>>): void => {
  if (caption !== null) {
    setSpinnerState({
      caption,
      [FormSpinnerFields.IsActive]: true,
    });
  } else {
    setSpinnerState(null);
  }
};

/**
 * Parses a stringified Auto-IT configuration into a strongly typed object.
 *
 * @param rawData - The raw JSON string stored in the configurationData field
 * @returns {ConfigurationDetailAutoIt} The parsed configuration object
 */

export const parseAutoItConfigurationFromJson = (rawData: string): ConfigurationDetailAutoIt => {
  // The rawData is a string BUT it is a JSON object controlled by the application. We can apply the JSON parse accordingly and it was
  // written from a ConfigurationDetailAutoIt so we expect those fields to be there. Parse the rawData.

  const parsed = JSON.parse(rawData);

  // And return the object accordingly.

  return {
    [ConfigurationFields.AccessKey]: parsed?.[ConfigurationFields.AccessKey] ?? '',
    [ConfigurationFields.BaseUrl]: parsed?.[ConfigurationFields.BaseUrl] ?? '',
    [ConfigurationFields.DatabaseId]: parsed?.[ConfigurationFields.DatabaseId] ?? 0,
    [ConfigurationFields.DeploymentTypeCode]: parsed?.[ConfigurationFields.DeploymentTypeCode] ?? '',
    [ConfigurationFields.SecretKey]: parsed?.[ConfigurationFields.SecretKey] ?? '',
  };
};

/**
 * Takes care of positioning the table according to the adjusted record count after a delete. The method takes in the totalCount 
 * before the record was deleted and then makes sure that the table's currentPage setting is in range or whether it needs to be 
 * moved back a page
 *
 * This table offers an example of the calculation to get the page range
 * 
 * currentPage	pageSize	skip	startCount	EndCount	-or-	currentPage	pageSize	skip	startCount	EndCount
 *       1         10       0         1       10                  1         5       0         1        5
 *       2         10      10        11       20                  2         5       5         6       10
 *       3         10      20        21       30                  3         5      10        11       15
 *       4         10      30        31       40                  4         5      15        16       20

 *
 * If the currentPage was 3 in the above 10 pageSize example we can see that the totalCount must be >= 21 for currentPage 3 to be
 * correct. If the totalCount was 21 prior to the delete then the currentPage would be 3. Deleting the record would reduce the 
 * totalCount to 20 and we can see that the currentPage of 3 would be out of bounds and would need to be reduced by 1. The rule 
 * works unless currentPage is 1 in which case we are happy to be on the first page with a blank table
 *
 * @param totalCount The total record count before the record was deleted
 * @param setTableControl The setter for the tableControl object
 * @param tableControl The tableControl object for the underlying crud
 */

export const positionAfterDelete = (setTableControl: React.Dispatch<React.SetStateAction<DataTableControl>>, tableControl: DataTableControl, totalCount: number) => {
  // Spread the currentPage and pageSize values

  const { currentPage, pageSize } = tableControl;

  // Set the newCurrentPage to the currentPage. Note we do this regardless of whether the currentPage needs to be changed as we have to
  // trigger a refresh of the table and changing the tableControl state guarantees the refresh required

  let newCurrentPage = currentPage;

  // Make sure we are not on the first page. If we are we don't need to worry about the record count as a blank first page is fine

  if (currentPage !== 1) {
    // We are not on the first page so we need to get the startCount value for the current page (see table above) so we can make
    // sure the count will display records on this page

    const startCount = (currentPage - 1) * pageSize + 1;

    // Check to see that the records will display. Note that the totalCount value coming in is before the record was deleted
    // so we -1

    if (totalCount - 1 < startCount) {
      // The totalCount is less than the startCount. This means that the current page is beyond the dataset after the
      // delete. We can reduce the current page by one

      newCurrentPage = currentPage - 1;
    }
  }
  // And update the tableControl with the new current page value to guarantee a table refresh after the delete

  setTableControl((prevTableControl) => ({
    ...prevTableControl,
    currentPage: newCurrentPage,
  }));
};

/**
 * Perform user reset by dispatching necessary Redux actions.
 *
 * @param dispatch Redux dispatch function
 */

export const resetUserState = async (dispatch: AppDispatch) => {
  dispatch(authorisationLoadAuthorisation(null));
  dispatch(userLoadNonPortalProduct(null));
  dispatch(userLoadPortalProduct(null));
  dispatch(userLoadUser(null));
};

/**
 * Converts a ConfigurationDetailAutoIt object into a JSON string
 * for storage in the ConfigurationFields.ConfigurationData field.
 *
 * @param data - The Auto-IT configuration detail object to be stored
 * @returns {string} A JSON string ready for database storage
 */
export const serializeAutoItConfiguration = (data: ConfigurationDetailAutoIt): string => {
  return JSON.stringify({
    [ConfigurationFields.AccessKey]: data[ConfigurationFields.AccessKey],
    [ConfigurationFields.BaseUrl]: data[ConfigurationFields.BaseUrl],
    [ConfigurationFields.DatabaseId]: data[ConfigurationFields.DatabaseId],
    [ConfigurationFields.DeploymentTypeCode]: data[ConfigurationFields.DeploymentTypeCode],
    [ConfigurationFields.SecretKey]: data[ConfigurationFields.SecretKey],
  });
};

/**
 * Shapes raw address data into the Address interface format.
 *
 * @param {any} address - The raw address data to shape. Note that they use of any here is unavoidable as the address being passed in is
 * raw data and the createdAt and updatedAt values are Dates in the database but we use strings in all our models
 * @returns {AddressCrud | null} - The shaped address data or null if the input address is falsy.
 */

export const shapeAddressData = (address: AddressCrud | null): AddressCrud | null => {
  // See if we have an address, if not return a null
  if (!address) {
    return null;
  }
  // To be here we have an address so we can return it as an Address object.
  return {
    [AddressFields.AddressType]: address[AddressFields.AddressType],
    [AddressFields.AdminArea1]: address[AddressFields.AdminArea1],
    [AddressFields.AdminArea2]: address[AddressFields.AdminArea2],
    [AddressFields.Country]: address[AddressFields.Country],
    [AddressFields.Id]: address[AddressFields.Id],
    [AddressFields.Latitude]: address[AddressFields.Latitude],
    [AddressFields.Locality]: address[AddressFields.Locality],
    [AddressFields.Longitude]: address[AddressFields.Longitude],
    [AddressFields.PlaceId]: address[AddressFields.PlaceId],
    [AddressFields.PostalCode]: address[AddressFields.PostalCode],
    [AddressFields.Route]: address[AddressFields.Route],
    [AddressFields.StreetNumber]: address[AddressFields.StreetNumber],
    [AddressFields.SubPremise]: address[AddressFields.SubPremise],
  };
};
